import React, { useMemo, useState } from "react";
import {
  FaChevronDown,
  FaFilter,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import {
  KATA_GRADES,
  KATA_ROLE_OPTIONS,
  KATA_SORT_OPTIONS,
  KATA_TYPES,
} from "../data/kataOptions";

import {
  cn,
  getDepartmentByCode,
  getDepartmentLabel,
  getDepartmentsForRegion,
  getRegions,
  getRoleLabel,
} from "../utils/kataUtils";

const selectClassName =
  "h-12 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-400/10 dark:border-white/10 dark:bg-slate-800 dark:text-slate-200";

export default function KataFilters({
  filters,
  total,
  onChange,
  onLocationChange,
  onReset,
}) {
  const [mobileOpen, setMobileOpen] =
    useState(false);

  const regions = useMemo(() => getRegions(), []);

  const departments = useMemo(
    () => getDepartmentsForRegion(filters.region),
    [filters.region]
  );

  const activeFilters = useMemo(() => {
    const items = [];

    if (filters.q) {
      items.push({
        name: "q",
        label: `Recherche : ${filters.q}`,
      });
    }

    if (filters.region) {
      items.push({
        name: "region",
        label: filters.region,
      });
    }

    if (filters.departement) {
      items.push({
        name: "departement",
        label: getDepartmentLabel(
          filters.departement
        ),
      });
    }

    if (filters.passage_grade) {
      items.push({
        name: "passage_grade",
        label: filters.passage_grade,
      });
    }

    if (filters.kata) {
      items.push({
        name: "kata",
        label: filters.kata,
      });
    }

    if (filters.role_recherche) {
      items.push({
        name: "role_recherche",
        label: getRoleLabel(
          filters.role_recherche
        ),
      });
    }

    return items;
  }, [
    filters.q,
    filters.region,
    filters.departement,
    filters.passage_grade,
    filters.kata,
    filters.role_recherche,
  ]);

  const handleRegionChange = (region) => {
    onLocationChange({
      region,
      departement: "",
    });
  };

  const handleDepartmentChange = (code) => {
    if (!code) {
      onLocationChange({
        region: filters.region,
        departement: "",
      });

      return;
    }

    const department = getDepartmentByCode(code);

    onLocationChange({
      region:
        department?.region || filters.region,
      departement: code,
    });
  };

  const removeFilter = (name) => {
    if (name === "region") {
      onLocationChange({
        region: "",
        departement: "",
      });

      return;
    }

    if (name === "departement") {
      onLocationChange({
        region: filters.region,
        departement: "",
      });

      return;
    }

    onChange(name, "");
  };

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900 sm:p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-orange-100 text-orange-600 dark:bg-orange-500/10 dark:text-orange-300">
            <FaFilter />
          </span>

          <div>
            <h2 className="font-semibold text-slate-950 dark:text-white">
              Rechercher un partenaire
            </h2>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              {total} annonce
              {total !== 1 ? "s" : ""} correspondant
              {total !== 1 ? "es" : "e"} à votre
              recherche
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            setMobileOpen((current) => !current)
          }
          className="flex h-11 items-center gap-2 rounded-xl bg-slate-100 px-4 text-sm font-semibold text-slate-700 dark:bg-white/10 dark:text-white lg:hidden"
          aria-expanded={mobileOpen}
          aria-controls="kata-filters-content"
        >
          Filtres

          {activeFilters.length > 0 ? (
            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-orange-500 px-1 text-xs text-white">
              {activeFilters.length}
            </span>
          ) : null}

          <FaChevronDown
            className={cn(
              "transition",
              mobileOpen && "rotate-180"
            )}
          />
        </button>
      </div>

      <div
        id="kata-filters-content"
        className={cn(
          "mt-5 space-y-5",
          !mobileOpen && "hidden lg:block"
        )}
      >
        <div className="relative">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

          <input
            type="search"
            value={filters.q}
            onChange={(event) =>
              onChange("q", event.target.value)
            }
            placeholder="Rechercher un judoka, un club, un kata ou un mot-clé…"
            aria-label="Rechercher dans les annonces"
            className="h-14 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-12 pr-12 text-sm font-semibold text-slate-800 outline-none transition placeholder:font-normal placeholder:text-slate-400 focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-400/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-slate-800"
          />

          {filters.q ? (
            <button
              type="button"
              onClick={() => onChange("q", "")}
              className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-200 hover:text-slate-800 dark:hover:bg-white/10 dark:hover:text-white"
              aria-label="Effacer la recherche"
            >
              <FaTimes />
            </button>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <FilterField label="Région">
            <select
              value={filters.region}
              onChange={(event) =>
                handleRegionChange(
                  event.target.value
                )
              }
              className={selectClassName}
            >
              <option value="">
                Toutes les régions
              </option>

              {regions.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Département">
            <select
              value={filters.departement}
              onChange={(event) =>
                handleDepartmentChange(
                  event.target.value
                )
              }
              className={selectClassName}
            >
              <option value="">
                Tous les départements
              </option>

              {departments.map((department) => (
                <option
                  key={department.code}
                  value={department.code}
                >
                  {department.code} ·{" "}
                  {department.departement}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Grade préparé">
            <select
              value={filters.passage_grade}
              onChange={(event) =>
                onChange(
                  "passage_grade",
                  event.target.value
                )
              }
              className={selectClassName}
            >
              <option value="">
                Tous les grades
              </option>

              {KATA_GRADES.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Kata travaillé">
            <select
              value={filters.kata}
              onChange={(event) =>
                onChange("kata", event.target.value)
              }
              className={selectClassName}
            >
              <option value="">
                Tous les katas
              </option>

              {KATA_TYPES.map((kata) => (
                <option key={kata} value={kata}>
                  {kata}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Rôle recherché">
            <select
              value={filters.role_recherche}
              onChange={(event) =>
                onChange(
                  "role_recherche",
                  event.target.value
                )
              }
              className={selectClassName}
            >
              <option value="">
                Tous les rôles recherchés
              </option>

              {KATA_ROLE_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Trier les résultats">
            <select
              value={filters.sort}
              onChange={(event) =>
                onChange("sort", event.target.value)
              }
              className={selectClassName}
            >
              {KATA_SORT_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </FilterField>
        </div>

        {activeFilters.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4 dark:border-white/10">
            <span className="mr-1 text-xs font-bold uppercase tracking-wide text-slate-400">
              Filtres actifs
            </span>

            {activeFilters.map((filter) => (
              <button
                key={filter.name}
                type="button"
                onClick={() =>
                  removeFilter(filter.name)
                }
                className="inline-flex min-h-9 items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 text-xs font-semibold text-orange-700 transition hover:border-orange-300 hover:bg-orange-100 dark:border-orange-400/20 dark:bg-orange-400/10 dark:text-orange-300"
                title={`Supprimer le filtre ${filter.label}`}
              >
                {filter.label}
                <FaTimes />
              </button>
            ))}

            <button
              type="button"
              onClick={onReset}
              className="ml-auto inline-flex min-h-9 items-center gap-2 px-2 text-xs font-semibold text-slate-500 transition hover:text-red-600 dark:text-slate-400 dark:hover:text-red-300"
            >
              <FaTimes />
              Tout effacer
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </span>

      {children}
    </label>
  );
}